export const TRAINING_STATUSES = ["rascunho", "ativo", "pausado", "concluido"] as const;
export const SESSION_STATUSES = ["planejada", "em_andamento", "realizada", "pulada"] as const;

export type TrainingStatus = (typeof TRAINING_STATUSES)[number];
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export interface Ordered {
  ordem?: number | null;
}

export function ordered<T extends Ordered>(rows: readonly T[] | null | undefined): T[] {
  return [...(rows ?? [])].sort((a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0));
}

export interface TrainingExercise extends Ordered {
  id: string;
  exercicio_nome: string;
  series: number | null;
  repeticoes: string | null;
  carga: string | null;
  descanso_segundos?: number | null;
  instrucoes?: string | null;
}

export interface TrainingDay extends Ordered {
  id: string;
  nome: string;
  observacoes?: string | null;
  treino_exercicios?: TrainingExercise[] | null;
}

export interface TrainingPhase extends Ordered {
  id: string;
  nome: string;
  objetivo?: string | null;
  duracao_semanas?: number | null;
  treino_dias?: TrainingDay[] | null;
}

export interface TrainingPlan {
  id: string;
  nome: string;
  status: string | null;
  versao: number;
  treino_fases?: TrainingPhase[] | null;
}

export interface NormalizedPhase extends TrainingPhase {
  treino_dias: (TrainingDay & { treino_exercicios: TrainingExercise[] })[];
}

export function phases(plan: TrainingPlan | null | undefined): NormalizedPhase[] {
  return ordered(plan?.treino_fases).map((phase) => ({
    ...phase,
    treino_dias: ordered(phase.treino_dias).map((day) => ({
      ...day,
      treino_exercicios: ordered(day.treino_exercicios),
    })),
  }));
}

export function planCounts(plan: TrainingPlan | null | undefined): {
  phases: number;
  days: number;
  exercises: number;
} {
  const normalized = phases(plan);
  return {
    phases: normalized.length,
    days: normalized.reduce((sum, phase) => sum + phase.treino_dias.length, 0),
    exercises: normalized.reduce(
      (sum, phase) =>
        sum + phase.treino_dias.reduce((daySum, day) => daySum + day.treino_exercicios.length, 0),
      0,
    ),
  };
}

export interface PlanPayloadExercise {
  exercicio_nome: string;
  series?: number | null;
  repeticoes?: string | null;
  carga?: string | null;
  descanso_segundos?: number | null;
  instrucoes?: string | null;
}

export interface PlanPayloadDay {
  nome: string;
  observacoes?: string | null;
  exercicios: PlanPayloadExercise[];
}

export interface PlanPayloadPhase {
  nome: string;
  objetivo?: string | null;
  duracao_semanas?: number | null;
  dias: PlanPayloadDay[];
}

export interface PlanPayload {
  nome: string;
  status: string;
  objetivo?: string | null;
  restricoes?: string | null;
  data_inicio?: string | null;
  data_fim_prevista?: string | null;
  fases: PlanPayloadPhase[];
}

/** Mesmas regras do editor legado; o RPC transacional revalida tudo no banco. */
export function validatePlan(payload: PlanPayload | null | undefined): string[] {
  const errors: string[] = [];
  if (String(payload?.nome ?? "").trim() === "") errors.push("Informe o nome do plano.");
  if (!TRAINING_STATUSES.includes(payload?.status as TrainingStatus))
    errors.push("Status inválido.");
  const phaseList = payload?.fases ?? [];
  if (phaseList.length === 0) errors.push("Adicione ao menos uma fase.");
  phaseList.forEach((phase, phaseIndex) => {
    if (String(phase.nome).trim() === "")
      errors.push(`Informe o nome da fase ${String(phaseIndex + 1)}.`);
    if (phase.dias.length === 0) errors.push(`Adicione um dia à fase ${String(phaseIndex + 1)}.`);
    phase.dias.forEach((day, dayIndex) => {
      if (String(day.nome).trim() === "")
        errors.push(`Informe o nome do dia ${String(dayIndex + 1)}.`);
      if (day.exercicios.length === 0)
        errors.push(`Adicione um exercício ao dia ${String(dayIndex + 1)}.`);
      day.exercicios.forEach((exercise, exerciseIndex) => {
        if (String(exercise.exercicio_nome).trim() === "")
          errors.push(`Informe o exercício ${String(exerciseIndex + 1)}.`);
      });
    });
  });
  return [...new Set(errors)];
}
